import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  DollarSign,
  Copy,
  ExternalLink,
  TrendingUp,
  Clock,
  CheckCircle,
  Percent,
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

function PartnerDashboardHome({ partner }) {
  const { t, isRTL } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/partner/dashboard`, {
        withCredentials: true
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getSignupUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/admin/signup?ref=${dashboardData?.partner?.referral_code}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">Welcome back, {dashboardData?.partner?.name}!</h1>
        <p className="text-purple-100">
          Your profit share is {dashboardData?.partner?.profit_share}% on all referred company payments
        </p>
      </div>

      {/* Referral Code Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-purple-600" />
            Your Referral Link
          </CardTitle>
          <CardDescription>
            Share this link with companies to earn commission on their payments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-gray-500 mb-1">Referral Code</p>
              <div className="flex items-center gap-2">
                <code className="text-2xl font-bold bg-purple-100 text-purple-700 px-4 py-2 rounded-lg">
                  {dashboardData?.partner?.referral_code}
                </code>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(dashboardData?.partner?.referral_code)}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Signup URL for Companies</p>
            <div className="flex items-center gap-2">
              <Input
                value={getSignupUrl()}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                variant="outline"
                onClick={() => copyToClipboard(getSignupUrl())}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Companies</p>
                <p className="text-2xl font-bold">{dashboardData?.stats?.total_companies || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Paid Companies</p>
                <p className="text-2xl font-bold">{dashboardData?.stats?.paid_companies || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Earnings</p>
                <p className="text-2xl font-bold">${dashboardData?.stats?.total_earnings?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pending Earnings</p>
                <p className="text-2xl font-bold">${dashboardData?.stats?.pending_earnings?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Companies */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Recent Companies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_companies?.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No companies yet. Share your referral link!</p>
            ) : (
              <div className="space-y-3">
                {dashboardData?.recent_companies?.map((company) => (
                  <div key={company.company_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{company.name}</p>
                      <p className="text-xs text-gray-500">{company.country || 'Unknown'}</p>
                    </div>
                    <Badge className={
                      company.subscription_status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }>
                      {company.subscription_status || 'free'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Payouts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Recent Payouts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData?.recent_payouts?.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No payouts yet. Earnings are calculated on the 15th of each month.</p>
            ) : (
              <div className="space-y-3">
                {dashboardData?.recent_payouts?.map((payout) => (
                  <div key={payout.payout_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">${payout.amount?.toFixed(2)}</p>
                      <p className="text-xs text-gray-500">
                        {payout.period_start} - {payout.period_end}
                      </p>
                    </div>
                    <Badge className={
                      payout.status === 'paid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-orange-100 text-orange-700'
                    }>
                      {payout.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Partner Info */}
      <Card>
        <CardHeader>
          <CardTitle>Partner Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t("common.name")}</p>
              <p className="font-medium">{dashboardData?.partner?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t("common.email")}</p>
              <p className="font-medium">{dashboardData?.partner?.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Company</p>
              <p className="font-medium">{dashboardData?.partner?.company_name || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Profit Share</p>
              <Badge className="bg-purple-100 text-purple-700">
                <Percent className="w-3 h-3 mr-1" />
                {dashboardData?.partner?.profit_share}%
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PartnerDashboardHome;
